'use client';

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { getMessages, sendMessage, markMessagesRead } from '@/lib/services/portalService';
import { createClient } from '@/lib/supabase/client';

interface Message {
  id: string;
  sender_id: string;
  sender_type: 'client' | 'admin';
  content: string;
  is_read: boolean;
  created_at: string;
  sender?: { full_name: string };
}

interface MessageFormValues {
  message: string;
}

interface MessagesTeaserProps {
  projectId?: string;
  userId?: string;
  userName?: string;
}

export default function MessagesTeaser({ projectId, userId, userName }: MessagesTeaserProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<MessageFormValues>();

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getMessages(projectId).then((data) => {
      setMessages(data || []);
      setLoading(false);
      // Mark admin messages as read
      markMessagesRead(projectId, 'admin');
    });

    // Real-time subscription
    const supabase = createClient();
    const channel = supabase
      .channel(`messages_${projectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `project_id=eq.${projectId}` }, (payload) => {
        setMessages((prev) => [...prev, payload.new as Message]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  const onSend = async (data: MessageFormValues) => {
    if (!projectId || !userId) return;
    setSending(true);
    try {
      const result = await sendMessage({
        projectId,
        senderId: userId,
        senderType: 'client',
        content: data.message,
        recipientContactNumber: '',
        recipientName: 'Marcus Aldridge',
        senderName: userName || 'Client',
        projectName: 'Your Project',
      });
      if (result) {
        reset();
        toast.success('Message sent — Marcus will be notified via WhatsApp.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read && m.sender_type === 'admin').length;

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="card flex flex-col">
      <div className="px-5 pt-5 pb-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            Messages
            {unreadCount > 0 && (
              <span className="bg-negative text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Direct line to your contractor</p>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-3 max-h-72 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="w-5 h-5 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Start the conversation!</p>
        ) : (
          messages.map((msg) => {
            const isClient = msg.sender_type === 'client';
            const initials = msg.sender?.full_name
              ? msg.sender.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
              : isClient ? 'CL' : 'MA';
            return (
              <div key={msg.id} className={`flex gap-2.5 ${isClient ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                  isClient ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'
                }`}>
                  {initials}
                </div>
                <div className={`flex-1 max-w-[85%] ${isClient ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                  <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    isClient
                      ? 'bg-primary text-primary-foreground rounded-tr-sm'
                      : msg.is_read
                      ? 'bg-muted text-foreground rounded-tl-sm'
                      : 'bg-accent/10 border border-accent/30 text-foreground rounded-tl-sm'
                  }`}>
                    {msg.content}
                  </div>
                  <span className="text-xs text-muted-foreground px-1">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="px-4 pb-4 pt-3 border-t border-border">
        <form onSubmit={handleSubmit(onSend)} className="flex gap-2">
          <div className="flex-1">
            <input
              {...register('message', { required: 'Message cannot be empty' })}
              className="form-input text-sm"
              placeholder="Type a message to Marcus..."
            />
            {errors.message && <p className="form-error">{errors.message.message}</p>}
          </div>
          <button
            type="submit"
            disabled={sending || !projectId}
            className="btn-primary px-3 py-2 flex-shrink-0"
            aria-label="Send message"
          >
            {sending ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}